import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';
import {
  Container, Paper, Box, Typography, TextField, Button,
  FormControl, InputLabel, Select, MenuItem, Alert,
  Avatar, Divider, Grid
} from '@mui/material';
import { SupportAgent, Send, Error as ErrorIcon, CheckCircle as SuccessIcon } from '@mui/icons-material';
import { deepPurple } from '@mui/material/colors';
import { useAuth } from '../services/auth/AuthContext';
import api from '../services/api';

const Ticket = () => {
  const [formData, setFormData] = useState({
    riderName: '',
    riderEmail: '',
    subject: '',
    type: 'general',
    message: '',
    urgency: 'medium'
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate UTSA email
    if (
      !formData.riderEmail.endsWith('@utsa.edu') &&
      !formData.riderEmail.endsWith('@my.utsa.edu')
    ) {
      setError('Please enter a valid UTSA email address');
      return;
    }

    // Validate required fields
    if (!formData.riderName || !formData.subject || !formData.message) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Make API call to backend using the configured api service
      const messageWithUrgency = `[Urgency: ${formData.urgency.toUpperCase()}]\n\n${formData.message}`;
      
      const response = await api.post('/tickets', {
        name: formData.riderName,
        email: formData.riderEmail,
        subject: formData.subject,
        type: formData.type,
        message: messageWithUrgency
        // Note: status defaults to 'open' in the backend model
        // urgency is not stored in the database but can be included in the message
      });

      setSuccess('Ticket created successfully! Redirecting...');
    setFormData({
      riderName: '',
      riderEmail: '',
      subject: '',
      type: 'general',
      message: '',
      urgency: 'medium'
    });
    setTimeout(() => navigate('/support'), 2000);
    } catch (err) {
      console.error('Ticket creation error:', err);
      setError(err.response?.data?.error || 'Failed to create ticket. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            position: 'relative' // Added for proper footer positioning
          }}>
            <Navbar/>
    <Container maxWidth="md" sx={{pt:10,
          mt: 4, 
          mb: 6,
          flex: 1, // Takes up all available space
          pb: 8 }}>
      <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ 
          backgroundColor: deepPurple[500], 
          color: 'white', 
          p: 3,
          display: 'flex',
          alignItems: 'center'
        }}>
          <Avatar sx={{ bgcolor: deepPurple[300], mr: 2 }}>
            <SupportAgent />
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight="bold">Create Support Ticket</Typography>
            <Typography variant="subtitle2">Create a ticket on behalf of a rider</Typography>
          </Box>
        </Box>

        <Box sx={{ p: 4 }}>
          {error && (
            <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" icon={<SuccessIcon />} sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Rider's Full Name"
                  name="riderName"
                  value={formData.riderName}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Rider's UTSA Email"
                  name="riderEmail"
                  type="email"
                  value={formData.riderEmail}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  margin="normal"
                  placeholder="user@utsa.edu"
                />
              </Grid>
            </Grid>

            <Grid container spacing={3} sx={{ mt: 0 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal" variant="outlined">
                  <InputLabel>Issue Type</InputLabel>
                  <Select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    required
                    label="Issue Type"
                  >
                    <MenuItem value="technical">Technical Issue</MenuItem>
                    <MenuItem value="renewal">Renewal Request</MenuItem>
                    <MenuItem value="general">General Question</MenuItem>
                    <MenuItem value="scooter">Scooter Problem</MenuItem>
                    <MenuItem value="safety">Safety Concern</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <FormControl fullWidth margin="normal" variant="outlined">
              <InputLabel>Urgency Level</InputLabel>
              <Select
                name="urgency"
                value={formData.urgency}
                onChange={handleChange}
                label="Urgency Level"
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="critical">Critical (Immediate Attention)</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Detailed Description"
              name="message"
              multiline
              rows={6}
              value={formData.message}
              onChange={handleChange}
              required
              variant="outlined"
              margin="normal"
              sx={{ mt: 2 }}
              placeholder="Describe the issue in detail. Include scooter ID if applicable."
            />

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                color="secondary"
                sx={{ mr: 2 }}
                onClick={() => navigate('/support')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                startIcon={<Send />}
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Ticket'}
              </Button>
            </Box>
          </form>
        </Box>
      </Paper>

      {/* Information Section */}
      <Paper elevation={2} sx={{ mt: 4, p: 3, backgroundColor: '#f9f9f9' }}>
        <Typography variant="h6" gutterBottom sx={{ color: deepPurple[700] }}>
          Ticket Creation Guidelines
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography variant="body1" paragraph>
          <strong>1. Verify Rider Information:</strong> Always confirm the rider's identity and UTSA email address.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>2. Be Specific:</strong> Include scooter ID numbers, timestamps, and detailed descriptions of issues.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>3. Urgency Levels:</strong>
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: '1rem', paddingLeft: '2rem' }}>
          <li><strong>Critical:</strong> Safety issues or scooters blocking pathways</li>
          <li><strong>High:</strong> Non-functional scooters or payment issues</li>
          <li><strong>Medium:</strong> General questions or renewal requests</li>
          <li><strong>Low:</strong> Feedback or non-urgent matters</li>
        </ul>
      </Paper>
    </Container>
    <Box sx={{ 
        width: '100%',
        bgcolor: 'background.paper',
        boxShadow: '0 -2px 5px rgba(0,0,0,0.1)',
        position: 'relative', // Changed from fixed to relative
        mt: 'auto' // Pushes footer to bottom
      }}>
        <Footer />
      </Box>
    </Box>
  );
};

export default Ticket;